import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import {
    EFilterType,
    IFilterFormItem,
    TFilterFormValue,
    TStringFilterValue,
} from 'common/types';
import {
    IContentTypeValue,
    IExtraCategoryOption,
    ExtraCategoryFiltersDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { MultiSelect } from 'common/components/FormField';

export function useExtraCategoryFilters(contentType: IContentTypeValue): {
    loading: boolean;
    filters: IFilterFormItem[];
} {
    const { showApolloError } = useApolloError();

    const { data, loading, error } = useQuery(ExtraCategoryFiltersDocument, {
        variables: { contentType },
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const { extraCategoriesFilters } = data || {};

    // Memoise so the array keeps a stable reference across renders (it only
    // changes when the query data does). Consumers depend on it in their own
    // memos, so an unstable reference would churn those downstream.
    const filters = useMemo<IFilterFormItem[]>(
        () =>
            extraCategoriesFilters
                ?.map((extraCategoryFilter) => {
                    const { id, name, categoryType, values } =
                        extraCategoryFilter;

                    if (
                        categoryType === IExtraCategoryOption.Datetime ||
                        categoryType === IExtraCategoryOption.Boolean
                    ) {
                        return null;
                    }

                    return {
                        key: `extraCategory-${id}`,
                        label: name,
                        fields: [
                            {
                                name: name,
                                label: name,
                                type: EFilterType.String,
                                initialValue: [],
                                component: MultiSelect,
                                componentProps: {
                                    options: values,
                                    InputProps: { label: name },
                                },
                                queryKey: 'extraCategories',
                                parseQueryValue: (values: TFilterFormValue) =>
                                    (values as TStringFilterValue[]).map(
                                        (value) => ({
                                            id: id,
                                            stringValue: value,
                                            // Both choice and string are handled as string
                                            type: IExtraCategoryOption.String,
                                        })
                                    ),
                            },
                        ],
                    } as IFilterFormItem;
                })
                .filter(Boolean) || [],
        [extraCategoriesFilters]
    );

    return {
        loading,
        filters,
    };
}
